import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/hairdresser-real.jpg";

export const hairdressers: Persona = {
  slug: "hairdressers-barbers",
  profession: { hu: "fodrász", en: "hairdresser" },
  professionPlural: {
    hu: "fodrászok és barberek",
    en: "hairdressers and barbers",
  },
  professionGenitive: { hu: "fodrászként", en: "as a hairdresser" },
  heroH1: {
    hu: "Tele szék, kevesebb telefon. Indulj 10 perc alatt.",
    en: "Full chairs, fewer phone calls. Launch in 10 minutes.",
  },
  heroSub: {
    hu: "Teljes körű AI asszisztens szóló fodrászoknak és szalonoknak: brand és weboldal, online foglalás és kártyák, Instagram- és TikTok-tartalom, Google Cégem és SEO, ügyfél-CRM és visszahívó kampányok — egy helyen.",
    en: "A full AI assistant for solo hairdressers and salons: brand and website, online booking and cards, Instagram and TikTok content, Google Business Profile and SEO, client CRM and win-back campaigns — all in one place.",
  },
  heroImage,
  heroImageAlt: "Hairdresser styling hair in the salon",
  chatExamplePrompt: {
    hu: "Tegyél be egy festést Annának holnap 14-re",
    en: "Book a color for Anna tomorrow at 2 PM",
  },
  chatExampleResponse: {
    hu: "Bejegyeztem holnap 14:00, 120 perc, hajfestés (alaphossz). Annának visszaigazolást küldtem.",
    en: "Booked tomorrow 2 PM, 120 minutes, hair color (base length). Confirmation sent to Anna.",
  },
  accentColor: "amber",

  defaultMode: "both-equal",
  teamSize: { min: 1, max: 10 },
  soloLabel: { hu: "Szóló fodrász", en: "Solo hairdresser" },
  teamLabel: {
    hu: "Szalon több fodrásszal",
    en: "Salon with multiple stylists",
  },

  soloDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Megnyitod a napot: 7 időpont, 1 új ügyfél. Az AI már mindenkit visszaigazolt.",
        en: "Open the day: 7 appointments, 1 new client. AI has already confirmed everyone.",
      },
    },
    {
      time: "11:00",
      what: {
        hu: "Új lead az Instagram-bióból. Az AI válaszolt az árakról, lefoglalta a próba-vágást.",
        en: "New lead from your Instagram bio. AI answered pricing questions, booked the trial cut.",
      },
    },
    {
      time: "14:00",
      what: {
        hu: "Egy ügyfél lemond. Az AI 60 mp alatt felajánlja a várólista első emberének, telik a slot.",
        en: "A client cancels. AI offers the slot to the first person on the waitlist within 60 seconds: filled.",
      },
    },
    {
      time: "17:00",
      what: {
        hu: "Új akció: '15% kedvezmény férfi vágásra csütörtök este'. Az AI Instagramon és emailben kiküldi.",
        en: "New promo: 'Thursday evening -15% off men's cuts'. AI sends it on Instagram and email.",
      },
    },
    {
      time: "20:00",
      what: {
        hu: "Napzárás: 11 ügyfél, 86 000 Ft, 2 új visszatérő. Számlák automatikusan kiállítva.",
        en: "End of day: 11 clients, 86,000 HUF, 2 new returners. Invoices issued automatically.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Szalon dashboard: 4 fodrászod 24 időpontot ad ma. Mindenki saját nézettel, te mindent látsz.",
        en: "Salon dashboard: your 4 stylists have 24 appointments today. Each has their own view, you see everything.",
      },
    },
    {
      time: "10:30",
      what: {
        hu: "Bence beteg. Az AI áthelyezi 5 ügyfelét Eszterhez és Tomihoz, mindenkit értesít.",
        en: "Bence is sick. AI moves his 5 clients to Eszter and Tomi, notifies everyone.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Új fodrász, Vivi érkezik. Felveszed a Bespire-be, 5 percen belül publikálódik a profilja a szalon oldalán.",
        en: "New stylist Vivi joins. You add her to Bespire: her profile is live on the salon site in 5 minutes.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Az AI mutatja: a balayage szolgáltatás +35% bevétel a héten. Megmutatod a többieknek.",
        en: "AI shows: balayage service is +35% revenue this week. You share it with the team.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Hétvégi 'menyasszony-csomag' akció telik. Az AI várólistára teszi a túljelentkezőket.",
        en: "Weekend 'bridal package' promo fills up. AI puts overflow on the waitlist.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Bevétel fodrászonként, jutalék-elszámolás kész, Számlázz.hu-integráció lefutott.",
        en: "Revenue per stylist, commission ready, Számlázz.hu integration completed.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: {
        hu: "Naptár és ügyfél: egy helyen",
        en: "Calendar and clients in one place",
      },
      body: {
        hu: "Nem kell papír, nem kell Excel, nem kell WhatsApp scroll. Mindent egy fiókban kezelsz.",
        en: "No paper, no Excel, no WhatsApp scrolling. Manage everything in one account.",
      },
    },
    {
      icon: "Bot",
      title: {
        hu: "Lead-űrlap + automata válasz",
        en: "Lead form + auto-response",
      },
      body: {
        hu: "Új érdeklődő kitölti a foglalási űrlapot, automatikus visszaigazoló email és foglalási link megy ki: neked már csak az ügyfelekre kell figyelni.",
        en: "New leads fill out a booking form, auto-confirmation email and link go out: you just focus on the client.",
      },
    },
    {
      icon: "Bell",
      title: { hu: "Automata emlékeztetők", en: "Automatic reminders" },
      body: {
        hu: "Email és SMS 24 órával előtte: drasztikusan csökken a no-show arány.",
        en: "Email and SMS 24 hours ahead: no-show rate drops drastically.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan fodrász, egy ár",
        en: "Unlimited stylists, one price",
      },
      body: {
        hu: "1 vagy 10 fő: ugyanaz a havidíj. Mindenki saját naptárral és ügyfélkörrel.",
        en: "1 or 10 people: same monthly fee. Each with their own calendar and clients.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Jogosultság-kezelés", en: "Role-based access" },
      body: {
        hu: "Tulaj, recepciós, fodrász: mindenki csak azt látja, amihez joga van.",
        en: "Owner, receptionist, stylist: everyone sees only what they're allowed to.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja az ügyfeleket", en: "AI assigns clients" },
      body: {
        hu: "Új lead: az AI specialitás (vágás, festés, kontyok) alapján a megfelelő fodrászhoz osztja.",
        en: "New lead: AI assigns by specialty (cut, color, updo) to the right stylist.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Automatikus jutalék-elszámolás",
        en: "Automatic commission settlement",
      },
      body: {
        hu: "Ki mennyit dolgozott, milyen bevételt hozott: exportálható, Számlázz.hu kompatibilis.",
        en: "Who worked how much, what revenue they brought in: exportable, Számlázz.hu compatible.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: { hu: "Okos foglalás és no-show védelem", en: "Smart booking and no-show protection" },
      body: {
        hu: "Festés + szárítás párhuzamosan, buffer time, előleg foglaláskor, 24 órás SMS/WhatsApp emlékeztető: kevesebb üres szék.",
        en: "Color + drying in parallel, buffer time, deposit at booking, 24-hour SMS/WhatsApp reminders: fewer empty chairs.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI social és előtte-utána galéria", en: "AI social and before-after gallery" },
      body: {
        hu: "Heti Instagram- és TikTok-poszt-terv a munkáidról, előtte-utána galéria automatikusan a weboldalra, hashtag- és leírás-javaslat: az AI megírja, te jóváhagyod.",
        en: "Weekly Instagram and TikTok post plan of your work, auto before-after gallery on your site, hashtag and caption suggestions: AI writes, you approve.",
      },
    },
    {
      icon: "Globe",
      title: { hu: "Google Cégem és helyi SEO", en: "Google Business Profile and local SEO" },
      body: {
        hu: "Az AI feltölti a Google Cégem-et új képekkel és posztokkal, blog- és szolgáltatás-oldalt ír a környékedre: a helyi keresők megtalálnak.",
        en: "AI keeps your Google Business Profile fresh with new photos and posts, writes blog and service pages targeting your area: local searchers find you.",
      },
    },
    {
      icon: "Mail",
      title: { hu: "CRM és visszahívó kampány", en: "CRM and win-back campaigns" },
      body: {
        hu: "Minden ügyfélnek profil, előzmény, kedvenc frizura. Ha 6 hete nem foglalt, az AI kedvezményes WhatsApp/e-mail üzenetet küld a nevedben.",
        en: "Every client gets a profile, history, favourite style. After 6 weeks with no booking, AI sends a discount WhatsApp/email on your behalf.",
      },
    },
  ],

  aiScope: [
    {
      categoryKey: "content",
      examples: [
        { hu: "Előtte-utána Instagram-karusszel a heti kedvenc munkádból", en: "Before-after Instagram carousel from this week's favourite work" },
        { hu: "TikTok-klip storyboard egy fade-hez vagy színezéshez", en: "TikTok clip storyboard for a fade or a color session" },
        { hu: "Ünnepi akció poszt és sztori-sablon egy klikkre", en: "Holiday promo post and story template in one click" },
      ],
    },
    {
      categoryKey: "seo",
      examples: [
        { hu: "Külön oldal minden szolgáltatásnak (férfi fade, balayage, szakáll)", en: "A dedicated page for each service (men's fade, balayage, beard)" },
        { hu: "Helyi blogcikkek: 'Legjobb barber shop a környéken' fókusszal", en: "Local blog articles focused on 'best barber shop near me'" },
        { hu: "Google Cégem posztok és képek automatán frissítve hetente", en: "Google Business posts and photos auto-refreshed weekly" },
      ],
    },
    {
      categoryKey: "email",
      examples: [
        { hu: "24 órás SMS/WhatsApp emlékeztető, előleg-igazolással", en: "24-hour SMS/WhatsApp reminder with deposit confirmation" },
        { hu: "6 hét után rebook-figyelmeztetés kedvezménnyel", en: "6-week rebook nudge with a discount" },
        { hu: "Születésnapi kupon egy nappal az esemény előtt", en: "Birthday coupon one day before the event" },
      ],
    },
    {
      categoryKey: "crm",
      examples: [
        { hu: "Kedvenc frizura, hajfesték-recept, allergia-jegyzet minden ügyfélnél", en: "Favourite style, color formula, allergy notes for every client" },
        { hu: "Utolsó látogatás dátuma és költése egy pillantásra", en: "Last visit date and spend at a glance" },
        { hu: "Forrás-követés: Instagram-poszt, Google-kereső, ajánlás", en: "Source tracking: Instagram post, Google search, referral" },
      ],
    },
  ],



  objections: [
    {
      question: {
        hu: "Több fodrász dolgozik nálunk: bonyolult lesz?",
        en: "We have multiple stylists: will it be complicated?",
      },
      answer: {
        hu: "Nem. Minden fodrásznak saját naptára, ügyféllistája, jutalék-statisztikája van. Te tulajdonosként mindent látsz, ők csak a sajátjukat. Korlátlan fodrászt vehetsz fel, az ár nem változik (1 vagy 10 fő: ugyanaz a 10 000 Ft + ÁFA / hó).",
        en: "No. Each stylist has their own calendar, clients and commission stats. As owner you see everything, they only see theirs. Add unlimited stylists, the price doesn't change (1 or 10 people, same 10,000 HUF + VAT / month).",
      },
    },
    {
      question: {
        hu: "Sok az idős ügyfelem, ők telefonon foglalnak",
        en: "Many of my clients are older and book by phone",
      },
      answer: {
        hu: "Maradhat a telefon: te vagy a recepciós a Bespire-ben jegyzi be 10 mp alatt. De a 24/7 online foglalás odavonzza a fiatalabb ügyfélkört is, akiknek 22:00-kor jut eszükbe lefoglalni a hétvégi vágást.",
        en: "Phone bookings stay: you or the receptionist enter them in Bespire in 10 seconds. But 24/7 online booking also attracts younger clients who remember at 10 PM to book a weekend cut.",
      },
    },
    {
      question: {
        hu: "A festés-vágás 3 órát is eltarthat: hogy kezeli a slot-okat?",
        en: "Color + cut can take 3 hours: how does it handle slots?",
      },
      answer: {
        hu: "Smart booking: festés alatt párhuzamosan tudsz vágni egy másik ügyfelet, az AI ezt automatikusan optimalizálja. Buffer time, technikai szünet, lemondás esetén újraosztás: minden automata.",
        en: "Smart booking: while color is processing you can cut another client, AI optimizes this automatically. Buffer time, technical breaks, re-assignment on cancellation, all automatic.",
      },
    },
    {
      question: {
        hu: "El tudom hozni a régi ügyfél-listámat?",
        en: "Can I bring my old client list?",
      },
      answer: {
        hu: "Igen. CSV/Excel importtal, vagy a Beüzemelés szolgáltatás keretében átvesszük a teljes ügyfélbázist, korábbi időpontokat, kedvenc kombinációkat.",
        en: "Yes. Via CSV/Excel import, or our Setup service migrates the full client base, past appointments and favorite combinations.",
      },
    },
  ],

  monetization: [
    {
      icon: "Scissors",
      title: { hu: "Vágás", en: "Cuts" },
      examples: [
        { hu: "Női vágás + szárítás", en: "Women's cut + blow-dry" },
        { hu: "Férfi vágás", en: "Men's cut" },
        { hu: "Gyerek vágás", en: "Kids' cut" },
      ],
      priceHint: {
        hu: "Példa: 90 vágás × 7 500 Ft = 675 000 Ft / hó (férfi 5:8e Ft, női 8:12e Ft)",
        en: "Example: 90 cuts × 7,500 HUF = 675,000 HUF / month (men 5:8k, women 8:12k HUF)",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "Festés és színezés", en: "Color and dye" },
      examples: [
        { hu: "Egy alapszín", en: "Single base color" },
        { hu: "Balayage / highlights", en: "Balayage / highlights" },
        { hu: "Komplett festés + vágás", en: "Full color + cut" },
      ],
      priceHint: {
        hu: "Példa: 30 festés × 32 000 Ft = 960 000 Ft / hó (vágás+festés 25:45e Ft)",
        en: "Example: 30 colors × 32,000 HUF = 960,000 HUF / month (cut+color 25:45k HUF)",
      },
    },
    {
      icon: "Heart",
      title: { hu: "Alkalmi szolgáltatások", en: "Occasion services" },
      examples: [
        { hu: "Menyasszonyi konty", en: "Bridal updo" },
        { hu: "Alkalmi konty", en: "Occasion updo" },
        { hu: "Frizura próba", en: "Hair trial" },
      ],
      priceHint: {
        hu: "Példa: 8 alkalom × 25 000 Ft = 200 000 Ft / hó (alkalmi konty 20:30e Ft)",
        en: "Example: 8 sessions × 25,000 HUF = 200,000 HUF / month (occasion updo 20:30k HUF)",
      },
    },
    {
      icon: "Package",
      title: { hu: "Termékek és kiegészítők", en: "Products and add-ons" },
      examples: [
        { hu: "Premium sampon-csomag", en: "Premium shampoo bundle" },
        { hu: "Otthoni ápolási kit", en: "Home-care kit" },
        { hu: "Ajándékkártya", en: "Gift card" },
      ],
      priceHint: {
        hu: "Passzív bevétel: ~90 000 Ft / hó",
        en: "Passive revenue: ~90,000 HUF / month",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Bérletek és tagságok", en: "Passes and memberships" },
      examples: [
        { hu: "6 alkalmas vágás-bérlet", en: "6-cut pass" },
        { hu: "Havi VIP (festés + vágás)", en: "Monthly VIP (color + cut)" },
        { hu: "Éves szépség-kártya", en: "Annual beauty card" },
      ],
      priceHint: {
        hu: "Szalon-szint: 4 fodrász × 30 bérletes ügyfél × 25 000 Ft = 3 000 000 Ft / hó potenciál",
        en: "Salon level: 4 stylists × 30 pass holders × 25,000 HUF = 3,000,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+48%",
      label: { hu: "új ügyfél / negyedév", en: "new clients / quarter" },
    },
    {
      value: "-70%",
      label: { hu: "telefonos lefoglalás", en: "phone bookings" },
    },
    {
      value: "4.8★",
      label: { hu: "Google vélemény-átlag", en: "Google review average" },
    },
    {
      value: "10 perc",
      label: { hu: "új ügyfél foglalása", en: "new-client booking" },
    },
  ],
  testimonial: {
    quote: {
      hu: "Régen telefonon foglaltunk, és a kollégák közben festettek. Most a Bespire-en mindenki online foglal, mi pedig csak hajat vágunk.",
      en: "We used to book by phone while the girls were coloring. Now everyone books online in Bespire: we just cut hair.",
    },
    name: "Varga Krisztina",
    role: { hu: "Hajstúdió-tulajdonos", en: "Hair studio owner" },
    city: { hu: "Győr", en: "Győr" },
    avatarInitials: "VK",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a szolgáltatásokat",
        en: "Tell us your services in chat",
      },
      body: {
        hu: "Férfi vágás, női festés, alkalmi konty: az AI árazza és időt rendel.",
        en: "Men's cut, women's color, occasion updo: AI prices them and assigns durations.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a szalon oldalt",
        en: "AI builds the salon site",
      },
      body: {
        hu: "Fodrász-profilok, foglalási szabályok, smart booking (festés alatt vágás).",
        en: "Stylist profiles, booking rules, smart booking (cutting during color processing).",
      },
    },
    {
      step: 3,
      title: {
        hu: "Importáljuk a törzsügyfél-listát",
        en: "We import your regulars",
      },
      body: {
        hu: "Korábbi szolgáltatások, preferenciák: egy kartonra.",
        en: "Past services, preferences: onto one card.",
      },
    },
    {
      step: 4,
      title: {
        hu: "Élő, online foglalás 24/7",
        en: "Live, online booking 24/7",
      },
      body: {
        hu: "QR-kód a kirakatban, link az Instagramon.",
        en: "QR code in the window, link on Instagram.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Stripe",
    "SimplePay",
    "Apple Pay",
    "Google Pay",
    "Meta",
    "WhatsApp Business",
  ],
  breadcrumbLabel: {
    hu: "Fodrászok és barberek",
    en: "Hairdressers & barbers",
  },
  similarPersonas: [
    "beauticians-beauty-salons",
    "nail-technicians",
    "massage-therapists",
  ],
};
